import { useState } from 'react';
import { AlertCircle, RefreshCw, XCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useApartments } from '../hooks/useApartments';
import { useReviews, useApproveReview, useRejectReview } from '../hooks/useReviews';
import { ReviewStatusBadge } from '../components/ReviewStatusBadge';
import { ReviewDetailPanel } from '../components/ReviewDetailPanel';
import { ListRefreshButton } from '../components/ListRefreshButton';
import { Review, ReviewStatus } from '../types/review';
import { formatDate } from '../utils/formatDate';
import { isAuthRequiredError } from '../utils/listRefreshState';

const Reviews = () => {
  const { t } = useTranslation(['common', 'pages', 'errors', 'forms', 'badges', 'navigation']);
  const [statusFilter, setStatusFilter] = useState<ReviewStatus>('pending');
  const [apartmentFilter, setApartmentFilter] = useState('');
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [page, setPage] = useState(0);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const { data: apartments = [] } = useApartments();

  const {
    data,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useReviews(
    {
      status: statusFilter,
      ...(apartmentFilter ? { apartmentId: apartmentFilter } : {}),
    },
    page,
  );

  const reviews = data?.content ?? [];
  const totalPages = data?.totalPages ?? 1;

  const { mutate: approve, isPending: isApproving } = useApproveReview();
  const { mutate: reject, isPending: isRejecting } = useRejectReview();

  const handleApprove = (id: string) => {
    approve(id, {
      onSuccess: () => {
        if (selectedReview?.id === id) setSelectedReview(null);
      },
    });
  };

  const handleReject = (id: string) => {
    reject(id, {
      onSuccess: () => {
        if (selectedReview?.id === id) setSelectedReview(null);
      },
    });
  };

  const getApartmentName = (id: string) =>
    apartments.find(a => a.id === id)?.name ?? id;

  const hasActiveFilters = apartmentFilter !== '';

  const handleRefresh = async () => {
    try {
      setRefreshError(null);
      await refetch({ throwOnError: true });
    } catch (error) {
      setRefreshError(isAuthRequiredError(error) ? t('common:signInRequired') : t('common:refreshFailed'));
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('pages:reviewsHeading')}</h1>
        <p className="text-gray-600 mt-2">{t('pages:reviewsSubHeading')}</p>
      </div>

      <div className="flex justify-end mb-4">
        <ListRefreshButton isRefreshing={isFetching} onRefresh={handleRefresh} />
      </div>

      {refreshError && (
        <div className="mb-6 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <AlertCircle size={18} className="shrink-0" />
          <span className="flex-1 text-sm">{refreshError}</span>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-1 text-sm font-medium hover:text-red-900 transition-colors"
          >
            <RefreshCw size={14} />
            {t('common:retry')}
          </button>
        </div>
      )}

      {/* ── Error banner ──────────────────────────────────────────────── */}
      {isError && (
        <div className="mb-6 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <AlertCircle size={18} className="shrink-0" />
          <span className="flex-1 text-sm">{t('errors:failedToLoadReviews')}</span>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1 text-sm font-medium hover:text-red-900 transition-colors"
          >
            <RefreshCw size={14} />
            {t('common:retry')}
          </button>
        </div>
      )}

      {/* ── Filters ───────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-3 mb-6">
        <select
          value={statusFilter}
          onChange={e => {
            setStatusFilter(e.target.value as ReviewStatus);
            setPage(0);
          }}
          className="filter-control"
          aria-label={t('common:allStatuses')}
        >
          <option value="pending">{t('badges:statusPending')}</option>
          <option value="published">{t('badges:statusPublished')}</option>
          <option value="rejected">{t('badges:statusRejected')}</option>
        </select>

        <select
          value={apartmentFilter}
          onChange={e => {
            setApartmentFilter(e.target.value);
            setPage(0);
          }}
          className="filter-control"
          aria-label={t('common:allApartments')}
        >
          <option value="">{t('common:allApartments')}</option>
          {apartments.map(apt => (
            <option key={apt.id} value={apt.id}>{apt.name}</option>
          ))}
        </select>

        {hasActiveFilters && (
          <button
            onClick={() => {
              setApartmentFilter('');
              setPage(0);
            }}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <XCircle size={14} />
            {t('common:clearFilters')}
          </button>
        )}
      </div>

      {/* ── Loading ───────────────────────────────────────────────────── */}
      {isLoading && (
        <div className="card flex justify-center py-16">
          <span className="inline-block w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* ── Table ─────────────────────────────────────────────────────── */}
      {!isLoading && (
        <>
          {reviews.length === 0 ? (
            <div className="card">
              <p className="text-gray-600 text-center py-12">{t('pages:noReviewsMatchFilters')}</p>
            </div>
          ) : (
            <div className="card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500 text-xs uppercase tracking-wide">
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnApartment')}</th>
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnCustomer')}</th>
                    <th className="pb-3 pr-4 font-medium text-right">{t('pages:columnRating')}</th>
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnStatus')}</th>
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnSubmitted')}</th>
                    <th className="pb-3 font-medium">{t('pages:columnActions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reviews.map(review => (
                    <tr
                      key={review.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setSelectedReview(review)}
                    >
                      <td className="py-3 pr-4 font-medium text-gray-900">
                        {getApartmentName(review.apartmentId)}
                      </td>
                      <td className="py-3 pr-4 text-gray-700">{review.customerName}</td>
                      <td className="py-3 pr-4 text-gray-600 text-right">{review.rating}</td>
                      <td className="py-3 pr-4">
                        <ReviewStatusBadge status={review.status} />
                      </td>
                      <td className="py-3 pr-4 text-gray-600 whitespace-nowrap">
                        {formatDate(review.createdAt.slice(0, 10))}
                      </td>
                      <td className="py-3">
                        {review.status === 'pending' && (
                          <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                            <button
                              onClick={() => handleApprove(review.id)}
                              disabled={isApproving || isRejecting}
                              className="text-xs font-medium text-green-600 hover:text-green-800 transition-colors px-2 py-1 rounded hover:bg-green-50 disabled:opacity-50"
                            >
                              {t('pages:approve')}
                            </button>
                            <button
                              onClick={() => handleReject(review.id)}
                              disabled={isApproving || isRejecting}
                              className="text-xs font-medium text-red-600 hover:text-red-800 transition-colors px-2 py-1 rounded hover:bg-red-50 disabled:opacity-50"
                            >
                              {t('pages:reject')}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Pagination ─────────────────────────────────────────────── */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-4 text-sm text-gray-600">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="flex items-center gap-1 px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label={t('common:previousPage')}
              >
                <ChevronLeft size={14} />
                {t('common:previous')}
              </button>
              <span aria-label="pagination">
                {t('common:pageOf', { current: page + 1, total: totalPages })}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="flex items-center gap-1 px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label={t('common:nextPage')}
              >
                {t('common:next')}
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      )}

      {/* ── Detail panel ──────────────────────────────────────────────── */}
      {selectedReview && (
        <ReviewDetailPanel
          review={selectedReview}
          apartmentName={getApartmentName(selectedReview.apartmentId)}
          isApproving={isApproving}
          isRejecting={isRejecting}
          onApprove={handleApprove}
          onReject={handleReject}
          onClose={() => setSelectedReview(null)}
        />
      )}
    </div>
  );
};

export default Reviews;
